import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RelatedItem, mapUsageSafetyFromDB } from "@/types/oil";
import { Product } from "@/types/product";

interface RelatedItemFromDB {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number | null;
}

function toRelatedItem(item: RelatedItemFromDB): RelatedItem {
  return {
    id: item.id,
    name: item.name,
    description: item.description || undefined,
    icon: item.icon || undefined,
    sortOrder: item.sort_order ?? undefined,
  };
}

function legacyToRelatedItems(items: string[]): RelatedItem[] {
  return items.map((name, index) => ({ id: `legacy-${index}`, name }));
}

const sortByOrder = (a: RelatedItem, b: RelatedItem) =>
  (a.sortOrder ?? 999) - (b.sortOrder ?? 999);

function mapDBToProduct(dbBase: any): Product {
  const categories = (dbBase.neutral_base_category_relations?.map(
    (rel: any) => toRelatedItem(rel.neutral_base_categories)
  ) || []).sort(sortByOrder);

  const propertiesFromRelations = (dbBase.neutral_base_property_relations?.map(
    (rel: any) => toRelatedItem(rel.neutral_base_properties)
  ) || []).sort(sortByOrder);

  const usesFromRelations = (dbBase.neutral_base_use_relations?.map(
    (rel: any) => toRelatedItem(rel.neutral_base_uses)
  ) || []).sort(sortByOrder);

  const safetyNotesFromRelations = (dbBase.neutral_base_safety_note_relations?.map(
    (rel: any) => toRelatedItem(rel.neutral_base_safety_notes)
  ) || []).sort(sortByOrder);

  return {
    id: dbBase.base_id,
    uuid: dbBase.id,
    name: dbBase.name,
    description: dbBase.description,
    safetyLevel: dbBase.safety_level,
    properties: propertiesFromRelations.length > 0 ? propertiesFromRelations : legacyToRelatedItems(dbBase.properties || []),
    uses: usesFromRelations.length > 0 ? usesFromRelations : legacyToRelatedItems(dbBase.uses || []),
    safetyNotes: safetyNotesFromRelations.length > 0 ? safetyNotesFromRelations : legacyToRelatedItems(dbBase.safety_notes || []),
    categories,
    imageUrl: dbBase.image_url || undefined,
    productType: "neutral_base",
    ...mapUsageSafetyFromDB(dbBase),
    baseType: dbBase.base_type,
    composition: dbBase.composition || [],
    brandName: dbBase.brand_name || undefined,
    brandOrigin: dbBase.brand_origin || undefined,
    volume: dbBase.volume || undefined,
    price: dbBase.price || undefined,
    certifications: dbBase.certifications || [],
    isVegan: dbBase.is_vegan || false,
    isCrueltyFree: dbBase.is_cruelty_free || false,
    isOrganic: dbBase.is_organic || false,
    freeFrom: dbBase.free_from || [],
    rating: dbBase.rating || undefined,
  };
}

async function fetchNeutralBasesData() {
  const { data, error } = await supabase
    .from("neutral_bases" as any)
    .select(`
      id, base_id, name, description, base_type,
      properties, uses, safety_level, safety_notes, image_url,
      composition, brand_name, brand_origin, volume, price,
      certifications, is_vegan, is_cruelty_free, is_organic, free_from, rating,
      can_aromatic, can_topical, can_internal,
      is_photosensitive, requires_dilution,
      safety_pregnant, safety_children, safety_babies, safety_pets,
      neutral_base_category_relations(
        neutral_base_categories(id, name, description, icon, sort_order)
      ),
      neutral_base_property_relations(
        neutral_base_properties(id, name, description, icon, sort_order)
      ),
      neutral_base_use_relations(
        neutral_base_uses(id, name, description, icon, sort_order)
      ),
      neutral_base_safety_note_relations(
        neutral_base_safety_notes(id, name, description, icon, sort_order)
      )
    `)
    .order("name", { ascending: true });

  if (error) throw error;
  return (data as any[]) || [];
}

export function useNeutralBases() {
  const { data: rawData = [], isLoading, error, refetch } = useQuery({
    queryKey: ["neutral-bases"],
    queryFn: fetchNeutralBasesData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const products = useMemo(() => rawData.map(mapDBToProduct), [rawData]);

  const categories = useMemo(() => {
    const all = new Set<string>();
    products.forEach(p => p.categories.forEach(c => all.add(c.name)));
    return ["Todos", ...Array.from(all).sort()];
  }, [products]);

  const searchProducts = useCallback((query: string): Product[] => {
    const lower = query.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(lower) ||
      p.description.toLowerCase().includes(lower) ||
      p.properties.some(pr => pr.name.toLowerCase().includes(lower))
    );
  }, [products]);

  const getById = useCallback((id: string) => products.find(p => p.id === id), [products]);

  return { products, categories, loading: isLoading, error, searchProducts, getById, refetch: async () => { await refetch(); } };
}
